import 'twin.macro';

import { useSessionStorage } from '@cheqroom/hooks';
import { Button, Card, Checkbox, Heading, Link, Stack, Text } from '@cheqroom/ui';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { useDoRefreshToken } from '../../api/authenticate.api';
import { useAcceptTermsOfUse } from '../../api/terms-and-conditions.api';
import { Step } from '../../App';
import { redirectToApplication } from '../PasswordStep/api.hooks';

const AcceptTermsStep = () => {
	const { t } = useTranslation('login');

	const [acceptedTerms, setAcceptedTerms] = useState(false);

	const { state } = useLocation() as {
		state: {
			accessToken: string;
			refreshToken: string;
			userId: string;
			workspaceId: string;
			redirectUri?: string;
			refetchAccessToken: () => Promise<{ access_token: string; refresh_token: string }>;
		};
	};
	const [redirectUri, _, clearStorage] = useSessionStorage<string>('CHEQROOM_REDIRECT_KEY');

	const { mutateAsync: acceptTermsAndConditions } = useAcceptTermsOfUse();
	const { mutateAsync: refreshToken } = useDoRefreshToken();

	const handleChangeAccept = (event: ChangeEvent<HTMLInputElement>) => {
		setAcceptedTerms(event.target.checked);
	};

	const handleContinueClick = async () => {
		const decodedToken: JwtPayload = jwtDecode(state.accessToken);

		let accessToken = state.accessToken;

		if (decodedToken.exp! < Date.now() / 1000) {
			const { access_token } = await refreshToken({ refreshToken: state.refreshToken });

			accessToken = access_token;
		}

		await acceptTermsAndConditions({
			userId: state.userId,
			accessToken,
		});

		redirectToApplication({
			accessToken,
			refreshToken: state.refreshToken,
			workspaceId: state.workspaceId,
			redirectUri,
			clearStorage,
		});
	};

	if (!state) {
		return <Navigate to={Step.ENTER_EMAIL} />;
	}

	return (
		<Card>
			<Card.Header>
				<Heading element="h2">{t('accept_terms_and_conditions_step.header')}</Heading>
			</Card.Header>
			<Card.Section>
				<Stack vertical spacing="loose">
					<Text>{t('accept_terms_and_conditions_step.subtext')}</Text>
					<Checkbox
						label={
							<Text size="small">
								<Trans
									i18nKey="accept_terms_and_conditions_step.accept"
									components={[
										<Link key="0" url="https://www.cheqroom.com/terms-of-use/" external inheritSize>
											Terms of Use
										</Link>,
									]}
								/>
							</Text>
						}
						onChange={handleChangeAccept}
						checked={acceptedTerms}
					/>
				</Stack>
			</Card.Section>
			<Card.Footer tw="flex justify-end">
				<Button disabled={!acceptedTerms} onClick={handleContinueClick}>
					{t('accept_terms_and_conditions_step.continue')}
				</Button>
			</Card.Footer>
		</Card>
	);
};

export default AcceptTermsStep;
